import axios from 'axios';

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000/api',
  timeout: 30000,
  // Don't set default Content-Type - let axios set it automatically
  // For FormData it will set multipart/form-data with boundary
  // For JSON objects it will set application/json
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    console.log('API Request:', config.url, 'Token:', token ? 'Present' : 'Missing');
    console.log('Full config:', config);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Authorization header added with token:', token.substring(0, 10) + '...');
    } else {
      console.log('No token available for Authorization header');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.log('API Response Error:', error.response?.status, error.response?.data);
    console.log('Full error object:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
    if (error.response?.status === 401) {
      // Only redirect if we're not already on the login page
      const currentPath = window.location.pathname;
      console.log('401 error on path:', currentPath);
      // Check if we should prevent auto-logout for debugging
      const preventAutoLogout = localStorage.getItem('preventAutoLogout') === 'true';
      console.log('Prevent auto logout:', preventAutoLogout);
      if (currentPath !== '/login' && !preventAutoLogout) {
        console.warn('Authentication failed, redirecting to login');
        // Token expired or invalid
        const token = localStorage.getItem('token');
        console.log('Token before removal:', token ? 'Present' : 'Missing');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Use React Router navigation instead of direct window.location
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
      } else if (preventAutoLogout) {
        console.log('Auto logout prevented for debugging');
      }
    }
    return Promise.reject(error);
  }
);

// Generic API functions
export const apiGet = async <T>(url: string, params?: any): Promise<T> => {
  const response = await api.get<T>(url, { params });
  return response.data;
};

export const apiPost = async <T>(url: string, data?: any, config?: any): Promise<T> => {
  const response = await api.post<T>(url, data, config);
  return response.data;
};

export const apiPut = async <T>(url: string, data?: any): Promise<T> => {
  const response = await api.put<T>(url, data);
  return response.data;
};

export const apiDelete = async <T>(url: string, config?: any): Promise<T> => {
  const response = await api.delete<T>(url, config);
  return response.data;
};

export const apiPatch = async <T>(url: string, data?: any): Promise<T> => {
  const response = await api.patch<T>(url, data);
  return response.data;
};

// Utility functions for handling API responses
export const handleApiResponse = (response: any) => {
  // Handle direct axios response
  if (response.data) {
    const data = response.data;
    if (data.success === false) {
      throw new Error(data.error || 'API request failed');
    }
    return data.success ? data.data : data;
  }
  
  // Handle already processed response
  if (response.success === false) {
    throw new Error(response.error || 'API request failed');
  }
  return response.success ? response.data : response;
};

export const handlePaginatedResponse = (response: any) => {
  // Handle direct axios response
  if (response.data) {
    const data = response.data;
    if (data.success === false) {
      throw new Error(data.error || 'API request failed');
    }
    return data;
  }
  
  // Handle already processed response
  if (response.success === false) {
    throw new Error('API request failed');
  }
  return response;
};

export default api;